import type React from 'react';
import { Kbd } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { ShortcutDefinition } from './keymap';

/**
 * Where the hint sits, which decides its look and what assistive tech hears:
 * - `'control'` — inside a button or menu item. Its own keycap surface
 *   (`Kbd` `default`), so it reads on a coloured button, and hidden from
 *   assistive tech, so the control keeps exactly its own name; the control
 *   exposes the key itself with `aria-keyshortcuts` (`renderWithKeyShortcuts`,
 *   or the plain prop where the component forwards it). The category
 *   filter's is on its trigger group: the design system gives the trigger's
 *   focusable inner button no prop to carry it.
 * - `'inline'` — next to a field, in a header or a notice. `Kbd` `light`,
 *   hidden from assistive tech: supplementary to the control that owns the key.
 * - `'list'` — a row that names the action beside it (the cheat sheet, the
 *   sign-in screen's `F1` row). `Kbd` `light`, read by assistive tech as
 *   its keys.
 */
export type ShortcutHintPlacement = 'control' | 'inline' | 'list';

type ShortcutHintProps = {
    shortcut: Pick<ShortcutDefinition, 'id' | 'keycapLabels'>;
    placement?: ShortcutHintPlacement;
    /** The shortcut can't act right now: the hint dims, the shared disabled look for keycap hints in `src/index.css`. */
    isDisabled?: boolean;
    className?: string;
};

/**
 * Renders a registry entry as a `Kbd`. Keycap text is literal Latin through
 * `Kbd.Content`, never localized; a combination's parts are joined with `+`.
 * It never names the action: the control it sits in, or the row's own text,
 * does.
 */
export const ShortcutHint: React.FC<ShortcutHintProps> = ({
    shortcut,
    placement = 'inline',
    isDisabled = false,
    className,
}) => (
    <Kbd
        aria-hidden={placement === 'list' ? undefined : true}
        className={className}
        data-disabled={isDisabled ? 'true' : undefined}
        variant={placement === 'control' ? 'default' : 'light'}
        {...getNodeTestIdProp(`shortcut-hint-${shortcut.id}`)}
    >
        <Kbd.Content>{shortcut.keycapLabels.join('+')}</Kbd.Content>
    </Kbd>
);
